import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import StepContent from '@mui/material/StepContent';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import PersonIcon from '@mui/icons-material/Person';
import moment from 'moment';
import { getApi } from 'views/services/api';

const ReservationHistoryDialog = ({ open, handleClose, reservationId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && reservationId) {
      fetchHistory();
    }
  }, [open, reservationId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await getApi(`api/reservation/history/${reservationId}`);
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch history', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE':
        return 'success';
      case 'UPDATE':
        return 'info';
      case 'PAYMENT':
        return 'warning';
      case 'CHECKIN':
        return 'primary';
      case 'CHECKOUT':
        return 'error';
      case 'ROOM_CHANGE':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'CREATE':
        return 'Reserva Creada';
      case 'UPDATE':
        return 'Actualización';
      case 'PAYMENT':
        return 'Pago Registrado';
      case 'CHECKIN':
        return 'Check-In';
      case 'CHECKOUT':
        return 'Check-Out';
      case 'ROOM_CHANGE':
        return 'Cambio de Habitación';
      default:
        return action;
    }
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="bold">
          Historial de Auditoría
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ minHeight: '400px', p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : history.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No hay historial registrado para esta reserva.
            </Typography>
          </Box>
        ) : (
          <Stepper orientation="vertical">
            {history.map((item, index) => (
              <Step key={index} active={true}>
                <StepLabel
                  icon={
                    <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: '0.75rem' }}>{history.length - index}</Avatar>
                  }
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" fontWeight="700">
                      {getActionLabel(item.action)}
                    </Typography>
                    <Chip
                      label={item.action}
                      size="small"
                      color={getActionColor(item.action)}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                      {moment(item.timestamp).format('DD MMM YYYY, h:mm A')}
                    </Typography>
                  </Box>
                </StepLabel>
                <StepContent>
                  <Box sx={{ bgcolor: 'background.default', p: 1.5, borderRadius: 1, mt: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {item.description}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <PersonIcon fontSize="small" color="action" />
                      <Typography variant="caption" color="text.secondary">
                        Realizado por:{' '}
                        <strong>
                          {item.performedBy ? `${item.performedBy.firstName} ${item.performedBy.lastName}` : 'Sistema / Desconocido'}
                        </strong>
                      </Typography>
                    </Box>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ReservationHistoryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  reservationId: PropTypes.string
};

export default ReservationHistoryDialog;
